import { z } from 'zod';
import { personIdSchema } from '../people/person.js';
import { agencyCalendarDate } from '../time/agency-dates.js';
import { uploadKinds, uploadMimeSchema } from '../uploads/allowlist.js';
import { fileCategoryLabels, fileCategorySchema, isSegregatedCategory } from './categories.js';
import { fileIdSchema } from './stored-file.js';
/**
 * One row of a person's file list (ADR-009 §4).
 *
 * `.strict()`, and deliberately without `blobName`, `container`, `store` or any URL: the list
 * is read from Cosmos, and the only way to the bytes is the authenticated content endpoint.
 * `categoryLabel` and `segregated` are derived from `category`, and a row where they disagree
 * does not parse.
 */
export const personFileListItemSchema = z
    .object({
    fileId: fileIdSchema,
    category: fileCategorySchema,
    categoryLabel: z.string().min(1),
    segregated: z.boolean(),
    contentType: uploadMimeSchema,
    byteLength: z.number().int().nonnegative(),
    committedAt: z.string().datetime(),
    displayName: z.string().min(1),
})
    .strict()
    .refine((item) => item.categoryLabel === fileCategoryLabels[item.category] && item.segregated === isSegregatedCategory(item.category), {
    message: 'categoryLabel and segregated are derived from category',
    path: ['category'],
});
export const personFilesResponseSchema = z
    .object({ personId: personIdSchema, files: z.array(personFileListItemSchema) })
    .strict();
/** `GET /api/people/{personId}/files/{fileId}/content` path parameters. */
export const personFileParamsSchema = z.object({ personId: personIdSchema, fileId: fileIdSchema }).strict();
/**
 * The content endpoint's query. Required, not defaulted: `inline` feeds the in-app viewer and
 * `attachment` is a download, and the two are audited as different actions.
 */
export const fileContentQuerySchema = z.object({ disposition: z.enum(['inline', 'attachment']) }).strict();
/**
 * The most the content endpoint will hold in memory or send (ADR-009 §4): the 20 MB upload cap.
 * The proxy buffers a whole version to verify its hash before sending, so this is also the bound
 * on what one request can make the Function App hold.
 */
export const FILE_CONTENT_MAX_BYTES = 20 * 1024 * 1024;
/**
 * The files a person's corrections have moved away (ADR-009 §3a, #194). DERIVED: the misfiled
 * record is never edited, so the list and content endpoints exclude it by reading this.
 */
export function correctedAwayFileIds(corrections) {
    return new Set(corrections.map((c) => c.misfiledFileId));
}
/**
 * What HR sees a file called, and what a download is named: the category label and the agency
 * calendar date it was committed (ADR-009 §4). Built by the server from ids and vocabulary only —
 * never `originalFileName`, which is what a person typed and may carry a name or a path.
 */
export function fileDisplayName(file) {
    return `${fileCategoryLabels[file.category]} ${agencyCalendarDate(file.committedAt)}`;
}
/** `fileDisplayName` plus the allowlisted type's own extension. */
export function fileDownloadName(file) {
    return `${fileDisplayName(file)}${uploadKinds[file.contentType].extensions[0] ?? ''}`;
}
/** One list row, shaped from a record. Nothing that locates the bytes is copied across. */
export function toPersonFileListItem(file) {
    return personFileListItemSchema.parse({
        fileId: file.id,
        category: file.category,
        categoryLabel: fileCategoryLabels[file.category],
        segregated: isSegregatedCategory(file.category),
        contentType: file.contentType,
        byteLength: file.byteLength,
        committedAt: file.committedAt,
        displayName: fileDisplayName(file),
    });
}
//# sourceMappingURL=api.js.map